import { Plus, Trash2, Copy } from "lucide-react";
import { findT, findA, triggerSummary, stepSummary, getList, setList } from "./catalog";

const CARD = "w-[320px] rounded-lg border bg-card text-left shadow-sm transition-[border-color,box-shadow] hover:border-[#C2A462]/60 hover:shadow-[0_0_0_1px_rgba(194,164,98,0.25)]";

function TriggerCard({ t, i, forms, sel, onOpen, onRemove }) {
  const T = findT(t.type);
  return (
    <div className={`${CARD} ${sel ? "border-[#C2A462]" : ""}`} data-testid={`wf-trigger-card-${i}`}>
      <button className="flex w-full items-start gap-3 p-4" onClick={onOpen}>
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-emerald-500/10 text-emerald-400"><T.icon className="h-5 w-5" /></span>
        <span className="min-w-0"><span className="block text-sm font-semibold text-emerald-400">{T.label}</span>
          <span className="block truncate text-xs text-muted-foreground">{t.name && t.name !== T.label ? t.name : T.desc}</span></span>
      </button>
      <div className="border-t px-4 py-2 text-xs text-muted-foreground line-clamp-2">{triggerSummary(t, forms)}</div>
      <div className="flex justify-end border-t px-2 py-1"><button onClick={onRemove} className="rounded p-1.5 hover:bg-destructive/10" data-testid={`wf-trigger-delete-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></button></div>
    </div>
  );
}

function Plus_({ onClick, tid }) {
  return (
    <div className="flex flex-col items-center">
      <div className="h-6 w-px bg-[#C2A462]/40" />
      <button onClick={onClick} className="grid h-8 w-8 place-items-center rounded-full border border-[#C2A462]/50 bg-background text-[#C2A462] transition-transform hover:scale-110" data-testid={tid}><Plus className="h-4 w-4" /></button>
      <div className="h-6 w-px bg-[#C2A462]/40" />
    </div>
  );
}

function StepCard({ s, tid, sel, onOpen, onCopy, onRemove }) {
  const A = findA(s.type);
  const sum = stepSummary(s);
  const logic = s.type === "wait" || s.type === "if_else";
  return (
    <div className={`${CARD} relative z-10 flex items-center gap-3 p-3 ${sel ? "border-[#C2A462]" : ""}`} data-testid={`wf-step-card-${tid}`}>
      <button className="flex min-w-0 flex-1 items-center gap-3" onClick={onOpen}>
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-md ${logic ? "bg-sky-500/10 text-sky-400" : "bg-[#C2A462]/10 text-[#C2A462]"}`}><A.icon className="h-5 w-5" /></span>
        <span className="min-w-0"><span className="block truncate text-sm font-medium">{s.name || A.label}</span>
          <span className="block truncate text-[11px] text-muted-foreground">{sum || (s.name && s.name !== A.label ? A.label : "")}</span></span>
      </button>
      <button onClick={onCopy} className="rounded p-1.5 hover:bg-muted" title="Duplicate" data-testid={`wf-step-copy-${tid}`}><Copy className="h-4 w-4" /></button>
      <button onClick={onRemove} className="rounded p-1.5 hover:bg-destructive/10" title="Delete" data-testid={`wf-step-delete-${tid}`}><Trash2 className="h-4 w-4 text-destructive" /></button>
    </div>
  );
}

const key = (path, i) => [...path, i].join("-");

function stepList(ctx, path) {
  const { steps, setSteps, panel, setPanel } = ctx;
  const list = getList(steps, path);
  const setL = (next) => setSteps(setList(steps, path, next));
  const groups = list.reduce((g, s, i) => {
    const last = g[g.length - 1];
    if (last && s.type !== "if_else" && last.items[0].type === s.type) last.items.push(s); else g.push({ start: i, items: [s] });
    return g;
  }, []);
  const isSel = (i) => panel?.kind === "step" && panel.i === i && panel.path.join() === path.join();
  const card = (s, i) => (
    <StepCard key={i} s={s} tid={key(path, i)} sel={isSel(i)} onOpen={() => setPanel({ kind: "step", path, i })}
      onCopy={() => setL([...list.slice(0, i + 1), JSON.parse(JSON.stringify(s)), ...list.slice(i + 1)])}
      onRemove={() => { setL(list.filter((_, j) => j !== i)); setPanel(null); }} />);
  return groups.map(({ start, items }) => (
    <div key={start} className="flex flex-col items-center">
      <div className="relative flex flex-wrap justify-center gap-4" data-testid={`wf-step-row-${key(path, start)}`}>
        {items.length > 1 && <div className="pointer-events-none absolute left-[160px] right-[160px] top-1/2 h-px bg-[#C2A462]/40" />}
        {items.map((s, k) => card(s, start + k))}
      </div>
      {items[0].type === "if_else" && (
        <div className="flex gap-8 border-t border-[#C2A462]/40" data-testid={`wf-branches-${key(path, start)}`}>
          {BRANCHES.map(([br, label, tone]) => { const bp = [...path, start, br]; return (
            <div key={br} className="flex min-w-[320px] flex-col items-center">
              <div className="h-5 w-px bg-[#C2A462]/40" />
              <span className={`rounded-full border px-3 py-0.5 text-xs ${tone}`}>{label}</span>
              <Plus_ onClick={() => setPanel({ kind: "pick-action", path: bp, at: 0 })} tid={`wf-add-step-${bp.join("-")}-0`} />
              {stepList(ctx, bp)}
              <span className="rounded-full bg-muted px-3 py-0.5 text-[10px] text-muted-foreground">then continue</span>
            </div>); })}
        </div>)}
      <Plus_ onClick={() => setPanel({ kind: "pick-action", path, at: start + items.length })} tid={`wf-add-step-${key(path, start + items.length)}`} />
    </div>
  ));
}

const BRANCHES = [["yes", "Yes", "text-emerald-400 border-emerald-500/40"], ["no", "No", "text-red-400 border-red-500/40"]];

export function Canvas({ w, forms, panel, setPanel, update }) {
  const setSteps = (s) => update({ steps: s });
  return (
    <div className="min-h-[560px] overflow-auto rounded-md border bg-[radial-gradient(circle,rgba(194,164,98,0.10)_1px,transparent_1px)] [background-size:22px_22px] p-8" data-testid="wf-canvas">
      <div className="mx-auto flex w-fit flex-col items-center">
        <div className="flex flex-wrap justify-center gap-4">
          {w.triggers.map((t, i) => <TriggerCard key={i} t={t} i={i} forms={forms} sel={panel?.kind === "trigger" && panel.i === i}
            onOpen={() => setPanel({ kind: "trigger", i })} onRemove={() => update({ triggers: w.triggers.filter((_, j) => j !== i) })} />)}
          <button onClick={() => setPanel({ kind: "pick-trigger" })} className="flex w-[320px] items-center gap-3 rounded-lg border-2 border-dashed border-[#C2A462]/50 bg-[#C2A462]/5 p-5 text-[#C2A462] transition-colors hover:bg-[#C2A462]/10" data-testid="wf-add-trigger">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-background"><Plus className="h-5 w-5" /></span><span className="font-medium">Add new trigger</span>
          </button>
        </div>
        <Plus_ onClick={() => setPanel({ kind: "pick-action", path: [], at: 0 })} tid="wf-add-step-0" />
        {stepList({ steps: w.steps, setSteps, panel, setPanel }, [])}
        <span className="rounded-full bg-muted px-4 py-1 text-xs font-medium text-muted-foreground" data-testid="wf-end">END</span>
      </div>
    </div>
  );
}
